import { Box, CloseButton, IconButton, Modal } from '@wix/design-system';
import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common';
import { useCallback, useEffect, useState } from 'react';

import type { FullscreenVideo } from '../../iframeMessageHandler';

import css from './index.module.scss';

type VideoFullscreenOverlayProps = {
  video: FullscreenVideo;
  onClose: () => void;
};

const OVERLAY_Z_INDEX = 999999;

const YOUTUBE_VIDEO_ID_PATTERN =
  /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|watch)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\r\n]{11})/;

const getYouTubeEmbedUrl = (url: string): string | null => {
  const match = url.match(YOUTUBE_VIDEO_ID_PATTERN);
  const videoId = match ? match[1] : null;
  return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : null;
};

const VideoFullscreenOverlay = ({ video, onClose }: VideoFullscreenOverlayProps) => {
  const { videos, index: initialIndex } = video;
  const [index, setIndex] = useState(initialIndex);
  const current = videos[index];
  const hasMultiple = videos.length > 1;

  const goTo = useCallback(
    (next: number) => setIndex((next + videos.length) % videos.length),
    [videos.length],
  );

  useEffect(() => {
    setIndex(initialIndex);
  }, [videos, initialIndex]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && hasMultiple) {
        goTo(index - 1);
      } else if (event.key === 'ArrowRight' && hasMultiple) {
        goTo(index + 1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goTo, index, hasMultiple]);

  const embedUrl = current ? getYouTubeEmbedUrl(current.url) : null;
  if (!current || !embedUrl) {
    return null;
  }

  return (
    <Modal
      isOpen
      contentLabel="video preview"
      zIndex={OVERLAY_Z_INDEX}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
    >
      <Box className={css.content}>
        <Box className={css.videoFrame}>
          <iframe
            key={current.id}
            src={embedUrl}
            title={current.id}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <Box className={css.closeButton}>
            <CloseButton size="small" skin="standardFilled" onClick={onClose} />
          </Box>
        </Box>
        {hasMultiple && (
          <Box className={`${css.navButton} ${css.navPrev}`}>
            <IconButton skin="standard" priority="secondary" size="medium" onClick={() => goTo(index - 1)}>
              <ChevronLeft />
            </IconButton>
          </Box>
        )}
        {hasMultiple && (
          <Box className={`${css.navButton} ${css.navNext}`}>
            <IconButton skin="standard" priority="secondary" size="medium" onClick={() => goTo(index + 1)}>
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default VideoFullscreenOverlay;
